import { supabase } from '@/lib/supabase/client';
import { Alert } from 'react-native';

interface CreateFamilyChatParams {
  weekendId: string;
  weekendName: string;
  createdBy: string;
}

/**
 * Creates a family group chat for a weekend and adds all non-dependent users as participants
 * @returns The created chat ID or null if failed
 */
export const createFamilyChat = async ({
  weekendId,
  weekendName,
  createdBy,
}: CreateFamilyChatParams): Promise<string | null> => {
  try {
    // Check if a family chat already exists for this weekend
    const { data: existingChat, error: checkError } = await supabase
      .from('chats')
      .select('id')
      .eq('weekend_id', weekendId)
      .eq('type', 'family')
      .maybeSingle();

    if (checkError) {
      console.error('Error checking existing family chat:', checkError);
      throw checkError;
    }

    if (existingChat) {
      console.log('Family chat already exists for this weekend:', existingChat.id);
      return existingChat.id;
    }

    // Get all non-dependent users (users with their own accounts)
    const { data: profiles, error: profilesError } = await supabase
      .from('profiles')
      .select('id')
      .or('is_dependent.is.null,is_dependent.eq.false');

    if (profilesError) {
      console.error('Error fetching profiles:', profilesError);
      throw profilesError;
    }

    if (!profiles || profiles.length === 0) {
      console.warn('No profiles found to add to family chat');
      return null;
    }

    // Create the family chat
    const { data: newChat, error: chatError } = await supabase
      .from('chats')
      .insert({
        weekend_id: weekendId,
        type: 'family',
        name: `Familie Weekend ${weekendName}`,
        description: `Groepschat voor alle familieleden van het ${weekendName}`,
        is_read_only: false,
        created_by: createdBy,
      })
      .select('id')
      .single();

    if (chatError) {
      console.error('Error creating family chat:', chatError);
      throw chatError;
    }

    // Add all users as participants
    const participants = profiles.map((profile) => ({
      chat_id: newChat.id,
      user_id: profile.id,
      is_admin: profile.id === createdBy, // Creator is admin
      is_muted: false,
    }));

    const { error: participantsError } = await supabase
      .from('chat_participants')
      .insert(participants);

    if (participantsError) {
      console.error('Error adding participants to family chat:', participantsError);
      // Try to clean up the chat if participants failed
      await supabase.from('chats').delete().eq('id', newChat.id);
      throw participantsError;
    }

    // Send a welcome message
    const { error: messageError } = await supabase.from('messages').insert({
      chat_id: newChat.id,
      sender_id: createdBy,
      content: `🎉 Welkom bij de Familie Weekend chat voor ${weekendName}! Hier kunnen we alles bespreken over het weekend.`,
      type: 'system',
    });

    if (messageError) {
      console.warn('Could not send welcome message:', messageError);
      // Non-critical, don't throw
    }

    console.log('Successfully created family chat:', newChat.id);
    return newChat.id;
  } catch (error) {
    console.error('Error in createFamilyChat:', error);
    return null;
  }
};

/**
 * Creates or ensures a family chat exists for a weekend
 * Shows an alert on success/failure
 */
export const ensureFamilyChat = async (
  weekendId: string,
  weekendName: string,
  createdBy: string
): Promise<void> => {
  const chatId = await createFamilyChat({
    weekendId,
    weekendName,
    createdBy,
  });

  if (chatId) {
    console.log('Family chat ready:', chatId);
  } else {
    console.error('Failed to create/get family chat');
  }
};

